import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import StatisticsCardLoading from '../../common/statistic-chart-card/StatisticsChartCardLoading';
import StatisticsChartEmpty from '../../common/StatisticsChartEmpty';
import { STATISTICS_EMPTY_CHART_GEO } from '../../common/lib/statisticsEmptyStateCopy';
import OrderPrimaryPhaseChinaMap from '../pipeline/OrderPrimaryPhaseChinaMap';
import { buildCompositionChartProvinceMapRows, filterOrdersForCompositionChart } from './lib/compositionChartBuckets';
import { COMPOSITION_CHART_DIMENSION, COMPOSITION_CHART_METRIC } from './lib/compositionChartConfig';

const COMPACT_MAP_HEIGHT_REM = 12;

const resolveMapCompactMode = (height = '20rem') => {
	const match = /^([\d.]+)rem$/.exec(String(height).trim());
	if (!match) return false;
	return Number(match[1]) < COMPACT_MAP_HEIGHT_REM;
};

const CompositionChartMap = ({
	orders = [],
	geoAggregates = null,
	dimension,
	metric = COMPOSITION_CHART_METRIC.COUNT,
	selectedPhase = null,
	isLoading = false,
	animationKey = 'default',
	height = '20rem',
	previewHeight = null,
	fillSlot = false,
	enableOrderDrill = false,
	onDrillSelect,
}) => {
	const isPreview = Boolean(previewHeight) || fillSlot;
	const slotHeight = previewHeight || height;
	const mapWrapperClassName = ['statistics-composition-chart', 'statistics-composition-chart--map', isPreview ? 'statistics-composition-chart--preview' : null]
		.filter(Boolean)
		.join(' ');

	const filteredOrders = useMemo(
		() => filterOrdersForCompositionChart(orders, { selectedPhase }),
		[orders, selectedPhase],
	);

	const provinceRows = useMemo(() => {
		if (dimension !== COMPOSITION_CHART_DIMENSION.PROVINCE) return [];
		if (!selectedPhase && Array.isArray(geoAggregates?.provinceRows) && geoAggregates.provinceRows.length > 0) {
			return geoAggregates.provinceRows.map((row) => ({
				name: row.name,
				count: Number(row.count) || 0,
				totalPrice: Number(row.totalPrice) || 0,
			}));
		}
		return buildCompositionChartProvinceMapRows(filteredOrders, metric);
	}, [dimension, filteredOrders, geoAggregates, metric, selectedPhase]);

	if (dimension === COMPOSITION_CHART_DIMENSION.CUSTOMER) {
		return (
			<div className={mapWrapperClassName}>
				<OrderPrimaryPhaseChinaMap
					orders={filteredOrders}
					geoAggregates={!selectedPhase ? geoAggregates : null}
					isLoading={isLoading}
					animationKey={`${animationKey}-customer-map`}
					height={slotHeight}
					compact={resolveMapCompactMode(slotHeight)}
					fillSlot={fillSlot}
					enableOrderDrill={enableOrderDrill}
					onDrillSelect={onDrillSelect}
				/>
			</div>
		);
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.PROVINCE) {
		if (isLoading) {
			if (isPreview) {
				return (
					<div className={mapWrapperClassName} style={{ height: '100%', width: '100%', minHeight: 0 }}>
						<StatisticsCardLoading className='statistics-lite-chart__loading statistics-lite-chart__loading--fill' fill />
					</div>
				);
			}

			return <StatisticsCardLoading className='statistics-lite-chart__loading' style={{ minHeight: slotHeight }} />;
		}

		if (!provinceRows.length) {
			return <StatisticsChartEmpty>{STATISTICS_EMPTY_CHART_GEO}</StatisticsChartEmpty>;
		}

		return (
			<OrderPrimaryPhaseChinaMap
				orders={filteredOrders}
				geoAggregates={!selectedPhase ? geoAggregates : null}
				provinceRowsOverride={provinceRows}
				provinceMetric={metric}
				isLoading={isLoading}
				animationKey={animationKey}
				height={slotHeight}
				compact={isPreview || resolveMapCompactMode(slotHeight)}
				fillSlot={fillSlot}
				wrapperClassName={mapWrapperClassName}
				chartInstanceKeyPrefix={`custom-province-map-${metric}`}
				enableOrderDrill={enableOrderDrill}
				onDrillSelect={onDrillSelect}
			/>
		);
	}

	return null;
};

CompositionChartMap.propTypes = {
	orders: PropTypes.array,
	geoAggregates: PropTypes.object,
	dimension: PropTypes.string.isRequired,
	metric: PropTypes.string,
	selectedPhase: PropTypes.string,
	isLoading: PropTypes.bool,
	animationKey: PropTypes.string,
	height: PropTypes.string,
	previewHeight: PropTypes.string,
	fillSlot: PropTypes.bool,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
};

export default memo(CompositionChartMap);
